import OauthDivider from "@/src/components/auth/oauth-divider";
import {
  markSessionAlive,
  setRememberMe,
} from "@/src/services/api/auth.api";
import { clearSessionCache } from "@/src/services/blocks/session";
import {
  SocialProvider,
  SsoConfigError,
  finishLogin,
  finishSocialLogin,
  getSocialProviders,
  getSsoFlow,
  startLogin,
  startSocialLogin,
} from "@/src/services/blocks/sso";
import { useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Image, ImageSourcePropType, Platform, Pressable, Text, View } from "react-native";

// Map the API's icon name string to local asset — no require() of a missing file
// on an unknown name (returns undefined → label-only button, no crash).
const PROVIDER_ICONS: Record<string, ImageSourcePropType> = {
  microsoft: require("../../assets/images/microsoft.webp"),
  google: require("../../assets/images/flat-color-icons_google.png"),
  github: require("../../assets/images/github-mark 1.png"),
};

type OauthButtonsProps = Readonly<{
  onSSOError?: (error: string, status?: string) => void;
  /**
   * The login screen's "Remember me" toggle. SSO has no toggle of its own, so the
   * one on the form governs both ways in — otherwise signing in with a provider
   * silently ignored a preference the user had just set.
   */
  rememberMe?: boolean;
  /**
   * Divider label i18n key. Defaults to "Or Sign in with" (assumes a login
   * method is rendered above). Pass an "only" variant when SSO is the sole
   * option, e.g. password login is hidden.
   */
  dividerLabel?: string;
}>;

/**
 * Renders one SSO button per provider returned by /auth/login-options.
 * Also owns the "Or Sign in with" divider so both hide atomically when there
 * are zero providers — no stranded divider, no flash while discovery is in flight.
 *
 * States:
 *   null  — discovery in flight; render nothing (prevents button-row flash)
 *   []    — no providers and no env fallback; render nothing
 *   [...] — one button per provider, preceded by the divider
 */
export default function OauthButtons({
  onSSOError,
  rememberMe = false,
  dividerLabel,
}: OauthButtonsProps) {
  const { t } = useTranslation();
  const router = useRouter();

  // null = loading (render nothing to avoid flash); [] = no providers
  const [providers, setProviders] = useState<SocialProvider[] | null>(null);

  useEffect(() => {
    getSocialProviders().then(setProviders);
  }, []);

  const handleSSOLogin = async (provider: SocialProvider) => {
    try {
      // Persisted BEFORE the hand-off: on web `startSocialLogin` navigates away
      // and never returns, so this is the last moment the toggle's value exists.
      // It is read back at the next cold start by `discardUnrememberedSession`.
      await setRememberMe(rememberMe);

      const result = await startSocialLogin(provider).catch((error) => {
        if (error instanceof SsoConfigError) return startLogin();
        throw error;
      });
      if (!result) return; // web navigated away, or the user cancelled on native

      // Which flow produced the code decides which endpoint exchanges it.
      await (getSsoFlow() === "oidc"
        ? finishLogin(result.code, result.state)
        : finishSocialLogin(result.code, result.state));
      clearSessionCache();
      // Native finishes the exchange here (web goes through `auth/callback`).
      markSessionAlive();
      router.replace("/");
    } catch (error: unknown) {
      const message =
        error instanceof SsoConfigError
          ? error.message
          : error instanceof Error && error.message
            ? error.message
            : t("ERROR_SSO_FAILED");
      onSSOError?.(message);
    }
  };

  // Still loading or no providers — render nothing (hides divider too)
  if (!providers || providers.length === 0) return null;

  const iconSize = Platform.OS === "web" ? 32 : 28;

  return (
    <>
      <OauthDivider label={dividerLabel} />
      <View className="flex-row justify-center gap-4 web:mb-4">
        {providers.map((provider) => {
          const iconName = provider.icon ?? provider.provider;
          const iconSource: ImageSourcePropType | undefined =
            PROVIDER_ICONS[iconName];
          // Capitalise first letter for display (API returns lower-case e.g. "microsoft")
          const rawLabel = provider.displayName || provider.provider;
          const label =
            rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
          return (
            <Pressable
              key={provider.provider}
              onPress={() => handleSSOLogin(provider)}
              className="flex-1 flex-row web:h-[150%] web:w-[16%] w-16 h-12 border gap-4 border-primary rounded-2xl justify-center items-center active:opacity-80 hover:bg-ash-3"
            >
              {iconSource && (
                <Image
                  source={iconSource}
                  style={{ width: iconSize, height: iconSize }}
                  resizeMode="contain"
                />
              )}
              <Text className="text-onyx">
                {t("LABEL_SIGN_IN_WITH_PROVIDER", { provider: label })}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </>
  );
}
